import { CareerPath, Goal, GoalType } from '../types';
import { generateMilestonesForType } from '../components/goals/goalComputedFields';
import { v4 as uuidv4 } from 'uuid';

interface SeededGoalConfig {
  goalType: GoalType;
  title: string;
  description: string;
  category: Goal['category'];
}

const CAREER_PATH_GOAL_MAP: Record<string, SeededGoalConfig[]> = {
  'climb-ladder': [
    { goalType: 'career-advancement', title: 'Advance to Next Level', description: 'Earn a promotion or advance to the next level in your current career track.', category: 'career' },
  ],
  'executive-track': [
    { goalType: 'career-advancement', title: 'Reach Executive Level', description: 'Build the skills, network, and track record needed to reach the C-suite.', category: 'career' },
    { goalType: 'networking', title: 'Build Executive Network', description: 'Develop relationships with senior leaders and mentors who can support your executive path.', category: 'networking' },
  ],
  'management-track': [
    { goalType: 'career-advancement', title: 'Transition to Management', description: 'Move from an individual contributor role into people management.', category: 'career' },
    { goalType: 'skill-acquisition', title: 'Develop Leadership Skills', description: 'Build core leadership capabilities: coaching, delegation, conflict resolution, and team building.', category: 'career' },
  ],
  'technical-specialist': [
    { goalType: 'skill-acquisition', title: 'Deepen Technical Expertise', description: 'Achieve deep mastery in your technical domain and become a recognized expert.', category: 'career' },
  ],
  'entrepreneurship': [
    { goalType: 'side-business', title: 'Launch My Business', description: 'Validate, build, and launch your own business from idea to first customers.', category: 'career' },
  ],
  'freelance-consulting': [
    { goalType: 'side-business', title: 'Build Consulting Practice', description: 'Establish a freelance or consulting practice with a steady pipeline of clients.', category: 'career' },
  ],
  'portfolio-career': [
    { goalType: 'career-advancement', title: 'Establish Portfolio Income', description: 'Build multiple income streams across different roles and projects.', category: 'career' },
    { goalType: 'side-business', title: 'Launch Side Income', description: 'Start generating income from a secondary professional activity.', category: 'financial' },
  ],
  'remote-first': [
    { goalType: 'work-life-balance', title: 'Secure Remote Work Arrangement', description: 'Transition to a fully remote or location-independent work setup.', category: 'career' },
  ],
  'work-life-balance': [
    { goalType: 'work-life-balance', title: 'Improve Work-Life Balance', description: 'Restructure your work habits and boundaries to achieve better life balance.', category: 'personal' },
  ],
  'fire': [
    { goalType: 'financial-target', title: 'Build FIRE Foundation', description: 'Establish the savings rate, investment strategy, and financial habits needed for financial independence.', category: 'financial' },
  ],
  'career-pivot': [
    { goalType: 'career-advancement', title: 'Execute Career Pivot', description: 'Successfully transition into a new industry or career field.', category: 'career' },
    { goalType: 'skill-acquisition', title: 'Build Pivot Skills', description: 'Acquire the skills and credentials needed for your target career.', category: 'career' },
  ],
  'international': [
    { goalType: 'relocation', title: 'Plan International Move', description: 'Research, plan, and prepare for an international career relocation.', category: 'career' },
  ],
  'academic-research': [
    { goalType: 'education', title: 'Pursue Academic Path', description: 'Prepare for and enter an academic or research career path.', category: 'credential' },
  ],
  'public-sector': [
    { goalType: 'career-advancement', title: 'Transition to Public Sector', description: 'Move into government, nonprofit, or public service work.', category: 'career' },
  ],
  'creative-industries': [
    { goalType: 'skill-acquisition', title: 'Build Creative Portfolio', description: 'Develop your creative skills and build a portfolio that opens doors in creative industries.', category: 'career' },
  ],
};

/**
 * Generate seeded goals from selected career paths.
 * Deduplicates by GoalType (first path wins).
 */
export function generateSeededGoals(paths: CareerPath[], targetYear: number): Goal[] {
  if (paths.length === 0) return [];

  const seenGoalTypes = new Set<GoalType>();
  const goals: Goal[] = [];
  const now = new Date().toISOString();
  const targetDate = `${targetYear}-12-31`;

  for (const path of paths) {
    const configs = CAREER_PATH_GOAL_MAP[path];
    if (!configs) continue;

    for (const config of configs) {
      if (seenGoalTypes.has(config.goalType)) continue;
      seenGoalTypes.add(config.goalType);

      const milestoneStrings = generateMilestonesForType(config.goalType, config.title);
      const milestones = milestoneStrings.map(title => ({
        id: uuidv4(),
        title,
        completed: false,
      }));

      goals.push({
        id: uuidv4(),
        title: config.title,
        description: config.description,
        category: config.category,
        targetDate: targetDate,
        status: 'not-started',
        progress: 0,
        milestones,
        priority: 'high',
        archived: false,
        goalType: config.goalType,
        createdAt: now,
        updatedAt: now,
      });
    }
  }

  return goals;
}
